import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useSelectedPlanContext } from "../contexts/SelectedPlanContext";
import { usePageSettings } from "../contexts/PageSettingsContext";
import { useMemo } from "react";
import { useCheckout } from "../contexts/CheckoutPageContext";

// Generalized fetch function with error handling
const fetchItems = async ({ queryKey }) => {
	const [, endpoint] = queryKey;
	try {
		const response = await axios.get(endpoint);
		return response.data;
	} catch (error) {
		throw new Error(`Failed to fetch data from ${endpoint}: ${error.message}`);
	}
};

export const useFetchServiceAddons = (serviceType) => {
	//Addons endpoint
	let endpoint = process.env.GATSBY_ADDONS_ENDPOINT + serviceType + `/addons`;
	if (!endpoint) {
		console.warn(`Unsupported service type: ${serviceType}`);
	}

	return useQuery({
		queryKey: ["serviceData", endpoint], // Pass the endpoint as part of queryKey
		queryFn: fetchItems, // Fetch function for addon items
		enabled: endpoint ? true : false, // Only fetch if there's a valid addon endpoint
		retry: 2, // Retry failed requests twice
	});
};

/**
 * Fetch function to post the payloadData to the API
 */
const postItems = async ({ queryKey }) => {
	const [_key, endpoint, payloadData] = queryKey;

	try {
		const response = await axios.post(endpoint, payloadData, {
			headers: { "Content-Type": "application/json" },
		});

		if (response.status === 200) {
			return response.data; // Adjust based on your API response structure
		} else {
			throw new Error(`Error: ${response.status}`);
		}
	} catch (error) {
		console.error("Error fetching payment cycles:", error);
		throw error;
	}
};

/**
 * Custom hook to fetch payment cycles based on service type, currency, and addons
 */
export const useFetchPaymentCycles = (currency, addonsIds = []) => {
	const { selectedPlan } = useSelectedPlanContext();
	const { selectedLocation } = usePageSettings();
	const { orderCount } = useCheckout();

	// Memoize payloadData to prevent unnecessary recalculations
	const { endpoint, payloadData, isValid } = useMemo(() => {
		const endpoint = `${process.env.GATSBY_PRICE_ENDPOINT}?currency=${currency}&count=${orderCount}`;
		let payloadData = {};
		let isValid = false;

		if (selectedPlan && selectedLocation && currency) {
			switch (selectedPlan.service) {
				case "colocation":
					if (selectedPlan.energy) {
						// Ensuring energy exists for colocation
						payloadData = {
							resources: [
								{
									location_code: selectedLocation.iata_code,
									service: "colocation",
									id_rack: selectedPlan.rack?.id,
								},
								{
									location_code: selectedLocation.iata_code,
									service: "colocation",
									id_prefix: selectedPlan.prefix?.id,
								},
								{
									location_code: selectedLocation.iata_code,
									service: "colocation",
									id_energy: selectedPlan.energy?.id,
								},
								{
									location_code: selectedLocation.iata_code,
									service: "colocation",
									id_bandwidth: selectedPlan.bandwidth?.id,
								},
							],
							count: orderCount,
						};
						isValid = true;
					} else {
						// console.warn("Colocation service selected but energy is missing.");
					}
					break;
				case "vps":
				case "baremetal":
					if (selectedPlan.id !== "") {
						payloadData = {
							resources: [
								{
									location_code: selectedLocation.iata_code,
									service: selectedPlan.service,
									id_cpu: selectedPlan.cpu?.id,
								},
								{
									location_code: selectedLocation.iata_code,
									service: selectedPlan.service,
									id_storage: selectedPlan.storage?.id,
								},
								{
									location_code: selectedLocation.iata_code,
									service: selectedPlan.service,
									id_ram: selectedPlan.ram?.id,
								},
								{
									location_code: selectedLocation.iata_code,
									service: selectedPlan.service,
									id_transfer: selectedPlan.transfer?.id,
								},
								{
									location_code: selectedLocation.iata_code,
									service: selectedPlan.service,
									id_bandwidth: selectedPlan.bandwidth?.id,
								},
							],
							cont: orderCount,
						};
						isValid = true;
					} else {
						// console.warn(`Service "${selectedPlan.service}" selected but plan ID is empty.`);
					}
					break;
				case "storage":
					payloadData = {
						resources: [
							{
								location_code: selectedLocation.iata_code,
								service: "storage",
								id_nas: selectedPlan.nas?.id,
							},
						],
						count: orderCount,
					};
					isValid = true;
					break;
				case "bgp":
					payloadData = {
						bgps: [selectedLocation.iata_code],
						count: orderCount,
					};
					isValid = true;
					break;
				case "ultraproxy":
					payloadData = {
						ultraproxies: [selectedLocation.iata_code],
						count: orderCount,
					};
					isValid = true;
					break;
				case "iptransit":
					payloadData = {
						resources: [
							{
								location_code: selectedLocation.iata_code,
								service: "iptransit",
								id_bandwidth: selectedPlan.bandwidth?.id,
							},
						],
						count: orderCount,
					};
					isValid = true;
					break;
				case "webhosting":
					payloadData = {
						plans: [selectedPlan?.id],
						count: orderCount,
					};
					isValid = true;
					break;

				default:
					console.warn(`Unsupported service type: ${selectedPlan.service}`);
					break;
			}

			// Add addons if any and if payloadData is valid
			if (addonsIds.length > 0 && isValid) {
				payloadData.addons = addonsIds; // Adjust formatting if your API expects a specific structure
			}
		} else {
			// console.warn("Missing required data to fetch payment cycles.");
		}

		// Ensure isValid is a boolean
		return { endpoint, payloadData, isValid: Boolean(isValid) };
	}, [selectedPlan, selectedLocation, currency, addonsIds]);

	return useQuery({
		queryKey: ["paymentCycles", endpoint, payloadData, addonsIds, orderCount],
		queryFn: postItems,
		enabled: isValid, // Must be a boolean
		retry: 2, // Retry failed requests twice
	});
};
